import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const personalities = [
  {
    name: "Mindfulness Coach",
    key: "guide helps in quick Stress relief, mindfulness, emotional balance",
    description: "Calm, peaceful, nature-connected",
    color: "#14B8A6",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/826c9ffd4257976971a8e3bad649c904f7a03dd263f425f6ab798b5a75141e16.png"
  },
  {
    name: "Confidence Coach",
    key: "Confidence, self-worth, motivation",
    description: "Casual, relatable, warm",
    color: "#FACC15",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/b0a782ead939fb625fd4e33032e723aa8528c7adec3343dbed027220453dad01.png"
  },
  {
    name: "Carer Mentor",
    key: "Career clarity, communication",
    description: "Wise, experienced, professional",
    color: "#6366F1",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/7ae80a0192a48d4fe31b8c625ed78bad7b4290254e83888ec3eb5e618261208a.png"
  },
  {
    name: " Productivity Mentor",
    key: "Productivity, discipline, goal setting ",
    description: "Energetic, dynamic, motivating",
    color: "#EF4444",
    image: "https://images.pexels.com/photos/4428040/pexels-photo-4428040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
];

export default function Landing({ onLogin }) {
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const loadingToastId = isLogin
      ? toast.loading("Please wait while the dashboard is loading...")
      : null;

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await axios.post(`${API}${endpoint}`, payload);
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }
      toast.success(isLogin ? "Welcome back!" : "Account created successfully!");
      onLogin(response.data.token, response.data.user);
    } catch (error) {
      if (loadingToastId) {
        toast.dismiss(loadingToastId);
      }
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-pink-50">
      <nav className="p-6 md:p-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <motion.h1 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-2xl md:text-3xl font-extrabold text-slate-800"
          >
            Your<span className="gradient-text">AI Mentor</span>
          </motion.h1>
          <Button 
            onClick={() => setShowAuth(true)} 
            className="btn-primary"
            data-testid="nav-get-started-btn"
          >
            Get Started
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 md:px-8 py-12 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight tracking-tight">
            Your Space to <span className="gradient-text">Reflect, Grow,</span><br />and Move Forward
          </h2>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Talk to an AI that feels like a guide, friend, mentor, and coach—helping you build confidence, better habits, and a stronger mindset.
          </p>
          <Button 
            size="lg" 
            onClick={() => setShowAuth(true)} 
            className="btn-primary text-lg px-10 py-6"
            data-testid="hero-get-started-btn"
          >
            Start Your Journey
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-20"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Choose Your <span className="gradient-text">Companion</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {personalities.map((persona, index) => (
              <motion.div
                key={persona.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <Card className={`overflow-hidden border-2 persona-${persona.key} card-hover cursor-pointer h-full`} data-testid={`personality-card-${persona.key}`}>
                  <div className="aspect-square overflow-hidden">
                    <img 
                      src={persona.image} 
                      alt={persona.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                    />
                  </div>
                  <div className="p-6">
                    <h4 className="text-xl font-bold mb-2" style={{ color: persona.color }}>
                      {persona.name}
                    </h4>
                    <p className="text-slate-600">{persona.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-3xl p-12 shadow-lg border border-slate-100"
        >
          <h3 className="text-3xl md:text-4xl font-bold text-center mb-8">
            What We Offer
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold mb-2">Meaningful Conversations</h4>
              <p className="text-slate-600">Chat with AI personalities designed to support your growth journey</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold mb-2">Self-Assessments</h4>
              <p className="text-slate-600">Reflect on your progress with guided self-assessment tools</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold mb-2">Daily Inspiration</h4>
              <p className="text-slate-600">Get personalized tips and affirmations to keep you motivated</p>
            </div>
          </div>
        </motion.div>
      </main>

      {showAuth && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="auth-modal">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl"
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold">{isLogin ? "Welcome Back" : "Create Account"}</h3>
              <button onClick={() => setShowAuth(false)} className="text-slate-400 hover:text-slate-600" data-testid="close-auth-modal-btn">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    type="text"
                    required={!isLogin}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1"
                    data-testid="auth-name-input"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1"
                  data-testid="auth-email-input"
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1"
                  data-testid="auth-password-input"
                />
              </div>

   {isLogin && (
  <div className="mt-2 text-right">
    <a
      href="/forgot-password"
      className="text-sm text-blue-600 hover:underline"
    >
      Forgot Password?
    </a>
  </div>
)}

              <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="auth-submit-btn">
                {loading ? (isLogin ? "Signing you in..." : "Creating account...") : isLogin ? "Sign In" : "Create Account"}
              </Button>
              {loading && isLogin && (
                <p className="text-xs text-slate-500 text-center mt-2">
                  Please wait while the dashboard is loading...
                </p>
              )}
            </form>

            <p className="text-center mt-6 text-sm text-slate-600">
              {isLogin ? "Don't have an account?" : "Already have an account?"}
              <button
                onClick={() => setIsLogin(!isLogin)}
                className="ml-2 text-orange-500 font-semibold hover:text-orange-600"
                data-testid="toggle-auth-mode-btn"
              >
                {isLogin ? "Sign Up" : "Sign In"}
              </button>
            </p>
          </motion.div>
        </div>
      )}
    </div>
  );
}
